import type { CleanedMetadata, ImageMetadataFormat, InspectedMetadata } from './metadataProcessing'

export type WorkerAction = 'inspect' | 'clean'
export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void
  addEventListener(type: 'message' | 'error', listener: EventListener): void
  removeEventListener(type: 'message' | 'error', listener: EventListener): void
  terminate(): void
}

type WorkerResult<T extends WorkerAction> = T extends 'clean'
  ? CleanedMetadata
  : InspectedMetadata

function abortError(): DOMException {
  return new DOMException('Operation aborted', 'AbortError')
}

export function createMetadataWorker(): WorkerLike {
  return new Worker(new URL('../worker/metadata.worker.ts', import.meta.url), {
    type: 'module',
  })
}

export function processMetadataInWorker<T extends WorkerAction>(
  bytes: Uint8Array,
  action: T,
  signal: AbortSignal,
  workerFactory: () => WorkerLike = createMetadataWorker,
): Promise<WorkerResult<T>> {
  if (signal.aborted) return Promise.reject(abortError())
  const worker = workerFactory()
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      signal.removeEventListener('abort', onAbort)
      worker.removeEventListener('message', onMessage)
      worker.removeEventListener('error', onError)
      worker.terminate()
    }
    const onAbort = () => {
      cleanup()
      reject(abortError())
    }
    const onMessage = ((event: MessageEvent) => {
      cleanup()
      const data = event.data as { result?: WorkerResult<T>; error?: string }
      if (data.error) reject(new Error(data.error))
      else resolve(data.result as WorkerResult<T>)
    }) as EventListener
    const onError = ((event: ErrorEvent) => {
      cleanup()
      reject(event.error instanceof Error ? event.error : new Error(event.message))
    }) as EventListener
    signal.addEventListener('abort', onAbort, { once: true })
    worker.addEventListener('message', onMessage)
    worker.addEventListener('error', onError)
    const transferred =
      bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
        ? bytes
        : bytes.slice()
    worker.postMessage({ action, buffer: transferred.buffer }, [transferred.buffer])
  })
}

export function loadImageUrl(src: string, signal: AbortSignal): Promise<HTMLImageElement> {
  if (signal.aborted) return Promise.reject(abortError())
  return new Promise((resolve, reject) => {
    const image = new Image()
    const cleanup = () => {
      signal.removeEventListener('abort', onAbort)
      image.onload = null
      image.onerror = null
    }
    const onAbort = () => {
      cleanup()
      image.src = ''
      reject(abortError())
    }
    image.onload = () => {
      cleanup()
      if (image.naturalWidth <= 0 || image.naturalHeight <= 0) reject(new Error('decode'))
      else resolve(image)
    }
    image.onerror = () => {
      cleanup()
      reject(new Error('decode'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
    image.src = src
  })
}

export function encodeJpegWithoutMetadata(image: HTMLImageElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const context = canvas.getContext('2d')
    if (!context) {
      reject(new Error('canvas_context'))
      return
    }
    context.drawImage(image, 0, 0)
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error('canvas_encode'))
      else if (blob.type !== 'image/jpeg') reject(new Error('wrong_mime'))
      else resolve(blob)
    }, 'image/jpeg', 0.95)
  })
}

export function outputMime(format: ImageMetadataFormat): string {
  return format === 'jpeg' ? 'image/jpeg' : `image/${format}`
}

export function outputFilename(inputName: string, format: ImageMetadataFormat): string {
  const base = inputName.replace(/\.[^.]+$/, '') || 'photo'
  return `${base}-clean.${format === 'jpeg' ? 'jpg' : format}`
}

export class ObjectUrlTracker {
  private current: string | null = null

  track(url: string): void {
    this.release()
    this.current = url
  }

  take(): string | null {
    const url = this.current
    this.current = null
    return url
  }

  release(expected?: string): void {
    if (!this.current || (expected !== undefined && expected !== this.current)) return
    URL.revokeObjectURL(this.current)
    this.current = null
  }
}
